import { Entity } from '@api/api/entity-schema';
import { RangeHorizon, ResampleIntervals } from '@chart';
import { Button } from '@design/components/button/Button';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useChartNavigate } from '@terminal/hooks/use-chart-navigate/useChartNavigate';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { queryKeys } from '@terminal/routes/query-params';
import { ChartSearchParamsAssetValue } from '@terminal/utils/chart-search-utils/chart-search-utils';
import {
  ChartPeerGroup,
  ChartPeerGroupActions,
} from '@terminal/widgets/chart-peer-group/ChartPeerGroup';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './RelativePerformance.module.scss';

const SPX_TAG = 'spx';

export const RelativePerformance = ({ entity }: { entity: Entity }) => {
  const { t } = useTranslation(['widget']);
  const chartNavigate = useChartNavigate();
  const { params } = useQueryParams();
  const chartActionsRef = useRef<ChartPeerGroupActions>(null);
  const competitorTag = params[queryKeys.competitorTag];

  const revampCompetitorTags = [
    ...(competitorTag ? [competitorTag] : []),
    SPX_TAG,
  ];

  const {
    data: competitorEntitiesData = [],
    isLoading: isLoadingCompetitorEntitiesData,
  } = useEntities({
    entityTags: revampCompetitorTags,
    enabled: !!revampCompetitorTags.length,
  });

  const peerEntities = revampCompetitorTags
    .map(tag => competitorEntitiesData.find(entity => entity.tag === tag))
    .filter(x => !!x);

  const onClick = () => {
    const visibleEntities = chartActionsRef.current?.getVisibleEntities() ?? [
      entity,
      ...peerEntities,
    ];
    const assets: ChartSearchParamsAssetValue[] = visibleEntities.map(asset => [
      asset.tag,
      asset.default_snake,
      0,
    ]);

    if (!assets.length) {
      return;
    }

    chartNavigate(assets, {
      horizon: RangeHorizon.OneYear,
      resample: ResampleIntervals.OneDay,
      yAxisType: 'merged',
    });
  };

  return (
    <div className={styles.sectionRow}>
      <div className={styles.headerWrapper}>
        <div className={styles.titleBase}>
          {t(
            'assetOverview.knowledgeGraph.competitors.tabs.competitorOverview.relativePerformance'
          )}
        </div>
        <WebOnlyFeat>
          <div className={styles.fullscreenButtonContainer}>
            <Button
              variant="empty"
              iconName="Expand"
              size="small"
              onClick={onClick}
              data-testid="open-full-chart"
            />
          </div>
        </WebOnlyFeat>
      </div>
      <div className={styles.chartWrapper}>
        <ChartPeerGroup
          entity={entity}
          variant="kg_competitors_drawer"
          isFetching={isLoadingCompetitorEntitiesData}
          peerEntities={peerEntities}
          actionsRef={chartActionsRef}
        />
      </div>
    </div>
  );
};
